/**
 * Firestore Database Service
 * 
 * Handles all database operations for submissions.
 * Each user (UID) can have only ONE submission.
 */

import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

const SUBMISSIONS_COLLECTION = 'submissions';

/**
 * Check if a submission exists for the given UID
 * @param {string} uid - User's anonymous UID
 * @returns {Promise<{exists: boolean, data: object|null}>}
 */
export const checkSubmissionExists = async (uid) => {
  try {
    const docRef = doc(db, SUBMISSIONS_COLLECTION, uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      console.log('Submission found for UID:', uid);
      return {
        exists: true,
        data: docSnap.data()
      };
    } else {
      console.log('No submission found for UID:', uid);
      return {
        exists: false,
        data: null
      };
    }
  } catch (error) {
    console.error('Error checking submission:', error);
    throw error;
  }
};

/**
 * Save a new submission to Firestore
 * @param {string} uid - User's anonymous UID
 * @param {object} submissionData - The submission data
 * @returns {Promise<void>}
 */
export const saveSubmission = async (uid, submissionData) => {
  try {
    const docRef = doc(db, SUBMISSIONS_COLLECTION, uid);
    
    const payload = {
      uid,
      ...submissionData,
      timestamp: serverTimestamp(), // Auto-generated server timestamp
      submittedAt: new Date().toISOString() // Client timestamp for backup
    };

    await setDoc(docRef, payload);
    console.log('Submission saved successfully for UID:', uid);
    
    return { success: true };
  } catch (error) {
    console.error('Error saving submission:', error);
    throw error;
  }
};

/**
 * Get submission data for a specific UID
 * @param {string} uid - User's anonymous UID
 * @returns {Promise<object|null>}
 */
export const getSubmission = async (uid) => {
  try {
    const docRef = doc(db, SUBMISSIONS_COLLECTION, uid);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting submission:', error);
    throw error;
  }
};
